import React, { createContext, useContext, useMemo } from 'react';

const BrandContext = createContext(null);

const MYL_BRAND = {
  id: 'myl',
  name: 'Manage Your Loans',
  source: 'MYL',
  showCostcoFields: false,
  heroBadge: 'Award-Winning Mortgage Broker 2025',
  heroTitle: 'Take Control of Your',
  heroTitleAccent: 'Financial Journey',
  heroSubtitle: 'We redefine the lending experience by putting you at the heart of every decision. Empowering you with insights throughout your loan journey.',
  ctaPrimary: 'Book a Consultation',
  ctaPrimaryHref: '#book-appointment',
  ctaSecondary: 'Explore Services',
  ctaSecondaryHref: '#services',
  navLinks: [
    { name: 'Services', href: '/#services' },
    { name: 'Process', href: '/process' },
    { name: 'Awards', href: '/awards' },
    { name: 'FAQ', href: '/#faq' },
    { name: 'Contact', href: '/contact' },
  ],
  showShopCardBanner: false,
  formHeading: 'Book Your Consultation',
  formSubheading: "Complete the form below and we'll be in touch shortly.",
};

const COSTCO_BRAND = {
  id: 'costco',
  name: 'Manage Your Loans — Costco Partner',
  source: 'Costco',
  showCostcoFields: true,
  heroBadge: 'Exclusive Costco Member Benefits',
  heroTitle: 'Your Exclusive',
  heroTitleAccent: 'Costco Loan Partner',
  heroSubtitle: "As Costco's exclusive finance partner, we offer tailored loan solutions PLUS Costco Shop Cards for Executive Members on settled loans!",
  ctaPrimary: 'Calculate Your Shop Card',
  ctaPrimaryHref: '/costco#calculator',
  ctaSecondary: 'Book Appointment',
  ctaSecondaryHref: '#book-appointment',
  navLinks: [
    { name: 'Home', href: '/costco' },
    { name: 'Services', href: '/costco#services' },
    { name: 'Shop Card', href: '/costco#calculator' },
    { name: 'FAQ', href: '/costco#faq' },
    { name: 'Contact', href: '/costco#book-appointment' },
  ],
  showShopCardBanner: true,
  formHeading: 'Book Your Consultation',
  formSubheading: "Complete the form below and we'll be in touch shortly.",
};

const COSTCO_HOSTNAMES = [
  'costco.manageyourloans.com.au',
  'costco.localhost',
];

function detectBrand() {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  const isCostcoHost = COSTCO_HOSTNAMES.some((h) => hostname === h);

  // Costco subdomain detection (production) — always Costco
  if (isCostcoHost) {
    return COSTCO_BRAND;
  }

  // /costco path on main domain — Costco branding for that page only, no session leak
  if (pathname.startsWith('/costco')) {
    return COSTCO_BRAND;
  }

  // Everything else on the main domain is MYL — clear any stale session flags
  sessionStorage.removeItem('myl_brand');
  return MYL_BRAND;
}

export function BrandProvider({ children }) {
  const brand = useMemo(() => detectBrand(), []);

  return (
    <BrandContext.Provider value={brand}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}

export { MYL_BRAND, COSTCO_BRAND };
